import { useRef, useState } from 'react';
import type { ForgeParams, MeshMode, PresetId } from '../core/types.ts';
import { PRESETS, presetById } from '../core/presets.ts';
import { useForgeStore } from '../state/store.ts';
import { captureRef, forgedMeshRef, useViewStore } from '../state/view.ts';
import { describeGLB, downloadBlob, exportGLB, exportOBJ } from '../core/export/glb.ts';
import { Dropzone } from './Dropzone.tsx';
import { CLIP_LABELS } from '../core/rig/animations.ts';
import { SheetForge } from './SheetForge.tsx';
import { HEAD_GALLERY } from '../core/samples.ts';
import { BlenderSendButton } from './BlenderStudio.tsx';

interface SliderProps {
  label: string;
  paramKey: keyof ForgeParams;
  min: number;
  max: number;
  step: number;
  format?: (v: number) => string;
}

function Slider({ label, paramKey, min, max, step, format }: SliderProps) {
  const value = useForgeStore((s) => s.params[paramKey] as number);
  const setParam = useForgeStore((s) => s.setParam);
  return (
    <label className="field">
      <span className="field-head">
        <span className="k">{label}</span>
        <span className="v">{format ? format(value) : value.toFixed(2)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setParam(paramKey as never, Number(e.target.value) as never)}
      />
    </label>
  );
}

function PresetPicker() {
  const preset = useForgeStore((s) => s.params.preset);
  const effective = useForgeStore((s) => s.effectivePreset);
  const setPreset = useForgeStore((s) => s.setPreset);
  const hint = presetById(preset === 'auto' ? effective : preset)?.hint;

  return (
    <div className="section">
      <h2>프리셋</h2>
      <div className="chips">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            className="chip"
            aria-pressed={preset === p.id}
            onClick={() => setPreset(p.id as PresetId)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="hint">
        {preset === 'auto' && <b>→ {presetById(effective)?.label} 선택됨. </b>}
        {hint}
      </p>
    </div>
  );
}

function HullSection() {
  const sideFacesLeft = useForgeStore((s) => s.params.sideFacesLeft);
  const setParam = useForgeStore((s) => s.setParam);
  const mesh = useForgeStore((s) => s.mesh);
  const backend = useForgeStore((s) => s.backend);
  const setBackend = useForgeStore((s) => s.setBackend);
  const share = mesh && 'viewShare' in mesh ? mesh.viewShare : null;
  const carvedViews = mesh && 'carvedViews' in mesh ? (mesh as { carvedViews: number }).carvedViews : 0;
  const occluded = mesh && 'occluded' in mesh ? (mesh as { occluded: number }).occluded : null;
  return (
    <div className="section">
      <h2>1 · 형상 — 비주얼 헐 + 깊이 깎기</h2>
      <p className="hint">
        정면·측면·후면 실루엣을 교차시켜 깎습니다. 뒷면이 앞면 거울상이 아니라 진짜 후면도가
        붙습니다. 오목한 곳(겨드랑이 안쪽 등)은 실루엣이 못 보므로 못 깎습니다.
      </p>
      <div className="chips" style={{ marginTop: 8 }}>
        <button className="chip" aria-pressed={sideFacesLeft} onClick={() => setParam('sideFacesLeft', true)}>
          측면: 왼쪽을 봄
        </button>
        <button className="chip" aria-pressed={!sideFacesLeft} onClick={() => setParam('sideFacesLeft', false)}>
          측면: 오른쪽을 봄
        </button>
      </div>
      <p className="hint">앞뒤가 뒤집혀 보이면 이걸 바꾸세요 — 텍스처가 반대 면에 붙은 것입니다.</p>
      <div style={{ marginTop: 10 }}>
        <Slider label="헐 해상도" paramKey="hullResolution" min={48} max={288} step={8} format={(v) => `${v}`} />
        <Slider label="표면 스무딩" paramKey="hullSmooth" min={0} max={8} step={1} format={(v) => `${v}회`} />
        <Slider label="요철 깊이" paramKey="hullRelief" min={0} max={0.08} step={0.002} format={(v) => `${(v * 100).toFixed(1)}%`} />
        <Slider label="깊이 요철 (코·눈두덩·갑옷 판)" paramKey="hullDepthRelief" min={0} max={0.08} step={0.005} format={(v) => (v === 0 ? '끔' : `높이의 ${(v * 100).toFixed(1)}%`)} />
        <Slider label="실루엣 팽창" paramKey="hullDilate" min={0} max={0.03} step={0.001} format={(v) => (v === 0 ? '없음' : `높이의 ${(v * 100).toFixed(1)}%`)} />
        <Slider label="실루엣 관용" paramKey="hullTolerance" min={0} max={2} step={1} format={(v) => (v === 0 ? '엄격 (교집합)' : `${v}뷰까지 불일치 허용`)} />
        <Slider label="깊이 깎기" paramKey="hullCarve" min={0} max={0.4} step={0.05} format={(v) => (v === 0 ? '끔' : `두께의 ${Math.round(v * 100)}%`)} />
      </div>
      <div className="chips" style={{ marginTop: 8 }}>
        <button className="chip ghost" aria-pressed={backend === 'heuristic'} onClick={() => setBackend('heuristic')}>
          깊이: 즉시 (오프라인)
        </button>
        <button className="chip ghost" aria-pressed={backend === 'onnx'} onClick={() => setBackend('onnx')}>
          깊이: 신경망 (Depth-Anything)
        </button>
      </div>
      <p className="hint">
        실루엣 헐은 배·턱 밑·팔과 몸통 사이 같은 오목부를 볼록하게 채웁니다 — Meshy 와 가장 크게 갈리는 지점입니다.
        깊이 깎기는 뷰마다 단안 깊이를 추정해 그 오목부를 두께의 일정 비율까지 안으로 깎습니다. 신경망(기본)은 첫 번에
        모델(약 50MB)을 받고 뷰 8장에 10초 안쪽. "즉시"는 셰이딩만 보므로 어두운 옷을 골로 오해할 수 있습니다.
        35% 를 넘기면 가는 부위(발목)가 끊어질 수 있습니다.
        {carvedViews > 0 && ` 지금 ${carvedViews}뷰로 깎았습니다.`}
      </p>
      {share && (
        <p className="hint">
          뷰 분포 — {Object.entries(share).map(([k, v]) => `${k} ${v.toLocaleString()}`).join(' · ')} 정점
          {occluded !== null && ` · 가림 판정으로 걸러진 (정점,뷰) ${(occluded * 100).toFixed(1)}%`}
        </p>
      )}
    </div>
  );
}

/** Meshy 의 리메시·텍스처 단계에 해당. 헐 → 베이크 → 감량 순으로 돈다. */
function RemeshSection() {
  const bake = useForgeStore((s) => s.params.bakeTexture);
  const target = useForgeStore((s) => s.params.targetTriangles);
  const setParam = useForgeStore((s) => s.setParam);
  const mesh = useForgeStore((s) => s.mesh);
  const timings = useForgeStore((s) => s.timings);
  const baked = mesh && 'baked' in mesh && mesh.baked;
  return (
    <div className="section">
      <h2>2 · 리메시 · 텍스처 베이크</h2>
      <div className="chips">
        <button className="chip" aria-pressed={bake} onClick={() => setParam('bakeTexture', true)}>UV 아틀라스로 굽기</button>
        <button className="chip" aria-pressed={!bake} onClick={() => setParam('bakeTexture', false)}>런타임 투영</button>
      </div>
      <div style={{ marginTop: 10 }}>
        <Slider label="목표 삼각형" paramKey="targetTriangles" min={0} max={150000} step={5000} format={(v) => (v === 0 ? '감량 안 함' : v.toLocaleString())} />
      </div>
      <p className="hint">
        뷰어의 N뷰 블렌드를 뷰별 차트 UV 아틀라스에 한 번 구워 넣습니다 — glb 가 뷰어와 같은 그림이 됩니다.
        감량은 QEM 엣지 붕괴로, 차트 경계는 잠가 텍스처가 안 샙니다. Meshy 기본값이 3만입니다.
        {mesh && ` 지금 ${mesh.triangleCount.toLocaleString()} 삼각형 · ${mesh.vertexCount.toLocaleString()} 정점${baked ? ' · 베이크됨' : ''}`}
        {timings.bake !== undefined && ` · 베이크 ${Math.round(timings.bake)}ms`}
        {timings.remesh !== undefined && ` · 감량 ${Math.round(timings.remesh)}ms`}
      </p>
    </div>
  );
}

function DepthSection() {
  const backend = useForgeStore((s) => s.backend);
  const setBackend = useForgeStore((s) => s.setBackend);
  const depth = useForgeStore((s) => s.depth);
  const meshMode = useForgeStore((s) => s.params.meshMode);
  const setParam = useForgeStore((s) => s.setParam);

  return (
    <div className="section">
      <h2>형상</h2>
      <div className="chips">
        <button
          className="chip"
          aria-pressed={meshMode === 'inflate'}
          onClick={() => setParam('meshMode', 'inflate' as MeshMode)}
        >
          부풀리기
        </button>
        <button
          className="chip"
          aria-pressed={meshMode === 'relief'}
          onClick={() => setParam('meshMode', 'relief' as MeshMode)}
        >
          릴리프
        </button>
      </div>

      <div className="chips" style={{ marginTop: 8 }}>
        <button
          className="chip ghost"
          aria-pressed={backend === 'heuristic'}
          onClick={() => setBackend('heuristic')}
        >
          즉시 (오프라인)
        </button>
        <button
          className="chip ghost"
          aria-pressed={backend === 'onnx'}
          onClick={() => setBackend('onnx')}
        >
          신경망 깊이
        </button>
      </div>
      <p className="hint">
        {backend === 'onnx'
          ? 'Depth Anything V2 Small을 브라우저에서 돌립니다. 첫 실행에 모델 약 50MB를 내려받고, 이후에는 캐시에서 꺼냅니다.'
          : '실루엣 거리장 + 셰이딩으로 즉시 계산합니다. 다운로드가 없고, 셀 셰이딩 원화에는 오히려 이쪽이 안정적입니다.'}
      </p>
      {depth?.fallbackReason && (
        <p className="notice" style={{ marginTop: 8 }}>
          신경망을 쓰지 못해 즉시 계산으로 대체했습니다 — {depth.fallbackReason}
        </p>
      )}
      {depth && !depth.fallbackReason && (
        <p className="hint">
          {depth.backend === 'onnx' ? `신경망(${depth.device ?? 'wasm'})` : '휴리스틱'} ·{' '}
          {Math.round(depth.ms)}ms
        </p>
      )}

      <div style={{ marginTop: 10 }}>
        <Slider label="격자 해상도" paramKey="resolution" min={32} max={320} step={8} format={(v) => `${v}`} />
        <Slider label="앞면 깊이" paramKey="depthScale" min={0} max={1.2} step={0.01} />
        <Slider label="부풀리기" paramKey="inflate" min={0} max={1} step={0.01} />
        <Slider label="스무딩" paramKey="smooth" min={0} max={12} step={1} format={(v) => `${v}회`} />
      </div>
    </div>
  );
}

function MaterialSection() {
  return (
    <div className="section">
      <h2>재질 추출</h2>
      <Slider label="디라이팅" paramKey="delight" min={0} max={1} step={0.01} />
      <Slider label="노멀 세기" paramKey="normalStrength" min={0} max={3} step={0.02} />
      <Slider label="러프니스 보정" paramKey="roughnessBias" min={-0.6} max={0.6} step={0.01} />
      <Slider label="메탈니스 보정" paramKey="metalnessBias" min={-0.2} max={1} step={0.01} />
      <Slider label="AO 세기" paramKey="aoStrength" min={0} max={1.5} step={0.02} />
      <Slider label="발광" paramKey="emissiveStrength" min={0} max={1.5} step={0.02} />
      <p className="hint">
        디라이팅은 원본에 구워진 조명을 걷어냅니다. 0이면 그림의 명암이 albedo에 그대로 남아
        우리 조명과 이중으로 겹칩니다.
      </p>
    </div>
  );
}

function StyleSection() {
  const rimColor = useForgeStore((s) => s.params.rimColor);
  const setParam = useForgeStore((s) => s.setParam);
  return (
    <div className="section">
      <h2>스타일</h2>
      <Slider label="툰 혼합" paramKey="toonMix" min={0} max={1} step={0.01} />
      <Slider label="툰 단계" paramKey="toonSteps" min={2} max={8} step={1} format={(v) => `${v}단`} />
      <Slider label="림라이트" paramKey="rimStrength" min={0} max={1.5} step={0.01} />
      <Slider label="이리데슨스" paramKey="iridescence" min={0} max={1.5} step={0.01} />
      <Slider label="뒷면 어둡게" paramKey="backTint" min={0} max={0.9} step={0.01} />
      <label className="field">
        <span className="field-head"><span className="k">림 색</span></span>
        <input
          type="color"
          value={rimColor}
          onChange={(e) => setParam('rimColor', e.target.value)}
        />
      </label>
      <p className="hint">이 항목들은 유니폼만 바꿉니다 — 재계산 없이 즉시 반영됩니다.</p>
    </div>
  );
}

function MotionSection() {
  const rig = useForgeStore((s) => s.rig);
  const clip = useForgeStore((s) => s.clip);
  const setClip = useForgeStore((s) => s.setClip);
  if (!rig) return null;
  return (
    <div className="section">
      <h2>모션 — 리깅 · 애니메이션</h2>
      <div className="chips">
        <button className="chip" aria-pressed={clip === null} onClick={() => setClip(null)}>정지</button>
        {rig.clips.map((c) => (
          <button key={c.name} className="chip" aria-pressed={clip === c.name} onClick={() => setClip(c.name)}>
            {CLIP_LABELS[c.name] ?? c.name}
          </button>
        ))}
      </div>
      <p className="hint">
        {rig.layout.humanoid
          ? '정면 실루엣에서 다리 둘·팔 둘을 찾아 17개 뼈를 세웠습니다. 클립 14개(이동 4·전투 6·반응 3·T포즈)가 glb 에 같이 나갑니다. 게임에서는 사망만 마지막 프레임에 멈추고 나머지 전투·반응 클립은 한 번 재생 뒤 대기로 돌아갑니다.'
          : '팔다리를 실루엣에서 못 찾아 비율 기본값으로 뼈를 세웠습니다. A포즈로 팔다리가 벌어진 그림이면 정확해집니다.'}
      </p>
    </div>
  );
}

function ViewSection() {
  const { autoRotate, wireframe, showEnvironment, exposure, toggle, setExposure } = useViewStore();
  return (
    <div className="section">
      <h2>보기</h2>
      <div className="chips">
        <button className="chip" aria-pressed={autoRotate} onClick={() => toggle('autoRotate')}>회전</button>
        <button className="chip" aria-pressed={wireframe} onClick={() => toggle('wireframe')}>와이어</button>
        <button className="chip" aria-pressed={showEnvironment} onClick={() => toggle('showEnvironment')}>배경</button>
      </div>
      <label className="field" style={{ marginTop: 8 }}>
        <span className="field-head">
          <span className="k">노출</span>
          <span className="v">{exposure.toFixed(2)}</span>
        </span>
        <input
          type="range"
          min={0.3}
          max={2.2}
          step={0.01}
          value={exposure}
          onChange={(e) => setExposure(Number(e.target.value))}
        />
      </label>
    </div>
  );
}

function ExportSection() {
  const mesh = useForgeStore((s) => s.mesh);
  const fileName = useForgeStore((s) => s.fileName);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  const base = (fileName ?? 'texel-forge').replace(/\.[^.]+$/, '');

  const saveGlb = async () => {
    const target = forgedMeshRef.current;
    if (!target) return;
    setBusy(true);
    try {
      const blob = await exportGLB(target, base);
      setLast(await describeGLB(blob));
      downloadBlob(blob, `${base}.glb`);
    } catch (err) {
      setLast(`실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const saveObj = () => {
    const target = forgedMeshRef.current;
    if (!target) return;
    downloadBlob(exportOBJ(target), `${base}.obj`);
  };

  const [thumbs, setThumbs] = useState<string[]>([]);
  const captureViews = () => {
    const shots = captureRef.current?.([0, 90, 180, 270]) ?? [];
    setThumbs(shots);
  };
  const saveThumbs = async () => {
    const names = ['front', 'right', 'back', 'left'];
    for (let i = 0; i < thumbs.length; i++) {
      const blob = await (await fetch(thumbs[i]!)).blob();
      downloadBlob(blob, `${base}-${names[i]}.png`);
      await new Promise((r) => setTimeout(r, 250));
    }
  };

  return (
    <div className="section">
      <h2>내보내기</h2>
      <div className="btn-row">
        <button className="btn primary" disabled={!mesh || busy} onClick={() => void saveGlb()}>
          {busy ? '굽는 중…' : '.glb 저장'}
        </button>
        <button className="btn" disabled={!mesh} onClick={saveObj}>.obj 저장</button>
        <button className="btn" disabled={!mesh} onClick={captureViews}>4뷰 썸네일</button>
        <BlenderSendButton />
      </div>
      {thumbs.length > 0 && (
        <>
          <div className="views-strip four">
            {thumbs.map((src, i) => (
              <div key={i} className="view-cell ok"><img src={src} alt={`${i * 90}°`} /><em>{i * 90}°</em></div>
            ))}
          </div>
          <button className="btn" style={{ marginTop: 6 }} onClick={() => void saveThumbs()}>썸네일 4장 저장</button>
        </>
      )}
      {last && <p className="hint" data-last-export>{last}</p>}
      <p className="hint">
        glb에는 지오메트리와 PBR 맵, 뼈대와 클립 14개가 함께 들어갑니다. 툰·림·이리데슨스는 glTF에 담을 칸이
        없어 빠집니다 — 엔진 쪽 셰이더에서 다시 얹으세요.
      </p>
    </div>
  );
}

/**
 * 머리 파츠 — 전신 8뷰에서 얼굴은 세로 120px 이다. 머리만 찍은 8뷰를 얹으면 같은 얼굴이 800px 로 온다.
 * 목 폭·높이로 배율과 위치를 잡고, 본체 머리는 오므려 새 머리 안에 숨긴다.
 */
function PartSection() {
  const part = useForgeStore((s) => s.part);
  const partInput = useForgeStore((s) => s.partInput);
  const loadPartFiles = useForgeStore((s) => s.loadPartFiles);
  const loadPartUrls = useForgeStore((s) => s.loadPartUrls);
  const clearPart = useForgeStore((s) => s.clearPart);
  const partScale = useForgeStore((s) => s.partScale);
  const setPartScale = useForgeStore((s) => s.setPartScale);
  const input = useRef<HTMLInputElement>(null);
  const gallery = HEAD_GALLERY;

  return (
    <div className="section">
      <h2>머리 파츠 (클로즈업 8뷰)</h2>
      <div className="chips">
        <button className="chip ghost" onClick={() => input.current?.click()}>머리 뷰 열기…</button>
        {gallery.map((g) => (
          <button key={g.id} className="chip ghost" onClick={() => void loadPartUrls(g.views, g.id)}>{g.label}</button>
        ))}
        {(part || partInput) && <button className="chip ghost" onClick={clearPart}>파츠 빼기</button>}
        <input ref={input} type="file" accept="image/*" multiple hidden
          onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length >= 2) void loadPartFiles(fs); e.target.value = ''; }} />
      </div>
      {(part || partInput) && (
        <label className="field" style={{ marginTop: 8 }}>
          <span className="field-head"><span className="k">크기 보정</span><span className="v">{partScale.toFixed(2)}×</span></span>
          <input type="range" min={0.7} max={1.4} step={0.01} value={partScale} onChange={(e) => setPartScale(Number(e.target.value))} />
        </label>
      )}
      {part ? (
        <p className="hint">
          <b>붙었습니다</b> — 삼각형 {part.triangleCount.toLocaleString()} · 배율 {part.fit.scale.toFixed(3)}
          (머리폭 {part.fit.detail.byHeadWidth.toFixed(2)} / 목폭 {part.fit.detail.byNeckWidth.toFixed(2)} / 높이 {part.fit.detail.byHeight.toFixed(2)})
          · 기준 t={part.fit.bodyAnchorT.toFixed(2)} · 목 t={part.fit.bodyNeckT.toFixed(2)} · 정수리 {part.fit.detail.topAfter.toFixed(2)}
        </p>
      ) : (
        <p className="hint">머리·목만 찍은 방위별 이미지 여러 장(파일 이름의 <code>-045</code> 로 방위를 읽습니다). 목 실루엣으로 본체에 맞춰 붙고, 본체 스켈레톤을 공유해 같은 클립으로 움직입니다.</p>
      )}
    </div>
  );
}

export function ControlPanel() {
  const hasImage = useForgeStore((s) => Boolean(s.analysis));
  const error = useForgeStore((s) => s.error);
  const mode = useForgeStore((s) => s.mode);

  return (
    <aside className="panel left">
      <Dropzone />
      {mode === 'sheet' && <SheetForge />}
      {error && <div className="error-box">{error}</div>}
      {hasImage && (
        <>
          <PresetPicker />
          {mode === 'sheet' ? <HullSection /> : <DepthSection />}
          {mode === 'sheet' && <RemeshSection />}
          <MaterialSection />
          <StyleSection />
          <PartSection />
          <MotionSection />
          <ViewSection />
          <ExportSection />
        </>
      )}
    </aside>
  );
}

